import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, Text } from '@fragments';
import { IconColorPicker } from '@tabler/icons-react';
import { normalizeColor } from '../../../model/schema';
import { PALETTE, SHADES, hexToHsv, hexToRgb, hslToRgb, hsvToHex, rgbToHex, rgbToHsl } from './color';

const clamp01 = value => Math.min(1, Math.max(0, value));
const FORMATS = ['hex', 'rgb', 'hsl'];

// A pointer drag on an element as fractions of its box (x, y in 0–1), from the press on.
function dragHandlers(onMove) {
  return {
    onPointerDown: event => {
      if (event.button !== 0) return;
      const element = event.currentTarget;
      element.setPointerCapture?.(event.pointerId);
      const move = next => {
        const rect = element.getBoundingClientRect();
        onMove(clamp01((next.clientX - rect.left) / rect.width), clamp01((next.clientY - rect.top) / rect.height));
      };
      const stop = () => { element.removeEventListener('pointermove', move); element.removeEventListener('pointerup', stop); element.removeEventListener('pointercancel', stop); };
      move(event);
      element.addEventListener('pointermove', move);
      element.addEventListener('pointerup', stop);
      element.addEventListener('pointercancel', stop);
    },
  };
}

// A number typed in a field, applied on Enter or when the field is left.
function Field({ value, onCommit, label, suffix, width = 40 }) {
  const [text, setText] = useState(String(value));
  useEffect(() => { setText(String(value)); }, [value]);
  const commit = () => { if (text !== String(value)) onCommit(text); };
  return <label className="art-fill__field" style={{ '--field-width': `${width}px` }}>
    <input value={text} aria-label={label} spellCheck={false} onChange={event => setText(event.target.value)} onBlur={commit}
      onKeyDown={event => { if (event.key === 'Enter') { event.currentTarget.blur(); } }} />
    {suffix && <span aria-hidden="true">{suffix}</span>}
  </label>;
}

function Swatches({ title, colors, value, onPick }) {
  if (!colors.length) return null;
  return <div className="art-fill__group">
    <Text.caption contrast={6}>{title}</Text.caption>
    <div className="art-fill__swatches">
      {colors.map(color => <button key={color} type="button" className={`art-fill__swatch ${color === value ? 'is-active' : ''}`} style={{ '--swatch': color }}
        aria-label={color} title={color} aria-pressed={color === value} onClick={() => onPick(color)} />)}
    </div>
  </div>;
}

// Ten Material hues as columns (2px apart), their shades as rows (touching), lightest on top.
function Palette({ value, onPick }) {
  const { t } = useTranslation('artEditor');
  return <div className="art-fill__group">
    <Text.caption contrast={6}>{t('colorPalette')}</Text.caption>
    <div className="art-fill__palette" role="group" aria-label={t('colorPalette')}>
      {PALETTE.map(column => <div key={column.hue} className="art-fill__palette-column">
        {column.shades.map((color, index) => <button key={color} type="button" className={`art-fill__palette-cell ${color === value ? 'is-active' : ''}`} style={{ '--swatch': color }}
          aria-label={`${t(`hue_${column.hue}`)} ${SHADES[index]}`} title={`${t(`hue_${column.hue}`)} ${SHADES[index]} · ${color}`} aria-pressed={color === value} onClick={() => onPick(color)} />)}
      </div>)}
    </div>
  </div>;
}

// A color with an optional opacity, edited like Figma's: saturation × brightness area, hue
// strip, opacity strip, the eyedropper, and HEX / RGB / HSL fields. Under them the brand's
// colors, the colors already in the art and the palette. `onChange({ color, alpha })`.
export default function SolidPicker({ color, alpha = 1, showAlpha = false, onChange, brand = [], used = [] }) {
  const { t } = useTranslation('artEditor');
  const hex = normalizeColor(color, '#ffffff');
  const [hsv, setHsv] = useState(() => hexToHsv(hex));
  const [format, setFormat] = useState('hex');
  const shown = useRef(hex);
  // An outside change (another stop, an undo) moves the controls; our own echo does not,
  // so the hue survives greys and black.
  useEffect(() => { if (hex !== shown.current) { shown.current = hex; setHsv(hexToHsv(hex)); } }, [hex]);
  const emit = (next, nextAlpha = alpha) => {
    setHsv(next);
    const value = hsvToHex(next);
    shown.current = value;
    onChange({ color: value, alpha: nextAlpha });
  };
  const pick = value => { const next = hexToHsv(value); emit(next.s === 0 || next.v === 0 ? { ...next, h: hsv.h } : next); };
  const area = dragHandlers((x, y) => emit({ ...hsv, s: x, v: 1 - y }));
  const hue = dragHandlers(x => emit({ ...hsv, h: Math.min(359.9, x * 360) }));
  const opacity = dragHandlers(x => emit(hsv, Math.round(x * 100) / 100));
  const nudge = event => {
    const step = event.shiftKey ? 0.1 : 0.01;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
    if (!moves[event.key]) return;
    event.preventDefault();
    emit({ ...hsv, s: clamp01(hsv.s + moves[event.key][0]), v: clamp01(hsv.v + moves[event.key][1]) });
  };
  const eyedropper = typeof window !== 'undefined' && 'EyeDropper' in window;
  const sample = async () => { try { const result = await new window.EyeDropper().open(); pick(result.sRGBHex.toLowerCase()); } catch { /* Cancelled. */ } };
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb);
  const number = (text, max) => { const value = Number(String(text).replace(',', '.').replace(/[^\d.]/g, '')); return Number.isFinite(value) ? Math.min(max, Math.max(0, value)) : null; };
  const setRgb = (key, text) => { const value = number(text, 255); if (value !== null) pick(rgbToHex({ ...rgb, [key]: value })); };
  const setHsl = (key, text) => { const value = number(text, key === 'h' ? 360 : 100); if (value !== null) pick(rgbToHex(hslToRgb({ ...hsl, [key]: key === 'h' ? value : value / 100 }))); };
  const setHex = text => { const value = normalizeColor(text.startsWith('#') ? text : `#${text}`, null); if (value) pick(value); };
  const setAlpha = text => { const value = number(text, 100); if (value !== null) emit(hsv, value / 100); };
  const brandColors = [...new Set(brand)];
  const usedColors = used.filter(item => !brandColors.includes(item));
  return <div className="art-fill__solid">
    <div className="art-fill__area" style={{ '--hue': `hsl(${Math.round(hsv.h)} 100% 50%)` }} {...area} role="slider" tabIndex={0} aria-label={t('colorArea')}
      aria-valuetext={hex} onKeyDown={nudge}>
      <span className="art-fill__thumb" style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: hex }} />
    </div>
    <div className="art-fill__strips">
      {eyedropper && <Button size="small" variant="text" color="overlay" preventLoading icon={<IconColorPicker />} aria-label={t('eyedropper')} tooltip={t('eyedropper')} onClick={sample} />}
      <div className="art-fill__strip-stack">
        <div className="art-fill__strip is-hue" {...hue} role="slider" aria-label={t('hue')} aria-valuemin={0} aria-valuemax={360} aria-valuenow={Math.round(hsv.h)} tabIndex={0}
          onKeyDown={event => { const step = event.shiftKey ? 10 : 1; if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); emit({ ...hsv, h: (hsv.h + (event.key === 'ArrowRight' ? step : -step) + 360) % 360 }); } }}>
          <span className="art-fill__thumb is-strip" style={{ left: `${(hsv.h / 360) * 100}%`, background: `hsl(${Math.round(hsv.h)} 100% 50%)` }} />
        </div>
        {showAlpha && <div className="art-fill__strip is-alpha" style={{ '--color': hex }} {...opacity} role="slider" aria-label={t('opacity')} aria-valuemin={0} aria-valuemax={100}
          aria-valuenow={Math.round(alpha * 100)} tabIndex={0}
          onKeyDown={event => { if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); emit(hsv, clamp01(Math.round((alpha + (event.key === 'ArrowRight' ? 0.01 : -0.01)) * 100) / 100)); } }}>
          <span className="art-fill__thumb is-strip" style={{ left: `${alpha * 100}%` }}><i style={{ background: hex, opacity: alpha }} /></span>
        </div>}
      </div>
    </div>
    <div className="art-fill__inputs">
      <Menu placement="bottom-start" items={FORMATS.map(item => ({ label: item.toUpperCase(), active: item === format, onClick: () => setFormat(item) }))}>
        <Button className="art-fill__format" size="small" variant="text" color="overlay" dropdown preventLoading aria-label={t('colorFormat')}>{format.toUpperCase()}</Button>
      </Menu>
      {format === 'hex' && <Field label="HEX" value={hex.slice(1).toUpperCase()} width={72} onCommit={setHex} />}
      {format === 'rgb' && ['r', 'g', 'b'].map(key => <Field key={key} label={key.toUpperCase()} value={Math.round(rgb[key])} width={34} onCommit={text => setRgb(key, text)} />)}
      {format === 'hsl' && ['h', 's', 'l'].map(key => <Field key={key} label={key.toUpperCase()} value={Math.round(key === 'h' ? hsl.h : hsl[key] * 100)} width={34} onCommit={text => setHsl(key, text)} />)}
      {showAlpha && <Field label={t('opacity')} value={Math.round(alpha * 100)} width={34} suffix="%" onCommit={setAlpha} />}
    </div>
    <Swatches title={t('colorBrand')} colors={brandColors} value={hex} onPick={pick} />
    <Swatches title={t('colorDocument')} colors={usedColors} value={hex} onPick={pick} />
    <Palette value={hex} onPick={pick} />
  </div>;
}
